"use client";

import { faUserPlus, faUserTie, faUserUnlock } from "@fortawesome/pro-solid-svg-icons";
import {
	PanelConfiguratorComponent,
	type PanelConfiguratorProps,
} from "@tosize/panel-configurator";
import type { PostData } from "@tosize/panel-configurator/src/context/PostDataContext";
import type { Workpiece as PanelWorkpiece } from "@tosize/panel-configurator/src/lib/workpiece";
import type { Workpiece } from "@tosize/shapes/src/types/workpiece";
import {
	Button,
	Dialog,
	type ProductGroup,
	ProductionUnit,
	SideDialog,
	type SideDialogProps,
	useNotifications,
} from "@tosize/ui";
import { useSearchParams } from "next/navigation";
import { Slot } from "radix-ui";
import { type Dispatch, type ReactNode, type SetStateAction, useMemo, useState } from "react";
import { match } from "ts-pattern";
import { updateCustomerProjectItem } from "~/app/[lang]/(default)/(auth)/actions";
import { CustomerProjectForm } from "~/app/[lang]/(default)/(auth)/customer/projects/CreateCustomerProjectForm";
import { CreateAccountForm } from "~/app/[lang]/(default)/(guest)/customer/account/(multicolumn)/create/Form";
import { ClientLoginForm } from "~/app/[lang]/(default)/(guest)/customer/account/(multicolumn)/login/Form";
import { onAddToProject } from "~/app/[lang]/cart/actions";
import { updateItem } from "~/app/[lang]/cart/actions/item/update";
import { useAddToCart } from "~/app/[lang]/cart/utils/useAddToCart";
import { useCommand } from "~/app/[lang]/command/Context";
import type { ServerPanelConfiguratorProps } from "~/components/configurator/utilities";
import { useBetterAction } from "~/hooks/useBetterAction";
import { useRouter } from "~/hooks/useRouter";

export interface BasePanelConfiguratorProps
	extends Pick<
		PanelConfiguratorProps,
		"mode" | "initialPostData" | "initialWorkpiece" | "onDataChange"
	> {
	item: {
		id?: string;
		quantity?: number;
		orderData?: string;
		parent?: boolean;
		product: {
			id?: string;
			groupedProductId?: string;
			attributeSet?: string;
		};
	};
}

export type VariantPanelConfiguratorProps =
	| {
			variant: "pdp" | "default";
			open?: never;
			onOpenChange?: never;
			title?: never;
			children?: never;
	  }
	| {
			variant: "dialogButton";
			open?: never;
			onOpenChange?: never;
			title: SideDialogProps["title"];
			children: ReactNode;
	  }
	| {
			variant: "dialog";
			open: SideDialogProps["open"];
			onOpenChange: SideDialogProps["onOpenChange"];
			title: SideDialogProps["title"];
			children?: never;
	  };

type Props = ServerPanelConfiguratorProps &
	BasePanelConfiguratorProps &
	VariantPanelConfiguratorProps;

export function BasePanelConfigurator(props: Props) {
	const {
		item,

		variant,
		open,
		onOpenChange,
		title,
		children,

		lang,
		cartId,
		apiToken,
		apiUrl,
		customer,
		messages,
		routes,
		settings,
		carrierCode,
		postcode,
		projects,

		cartMessages,
		innerMessages,
		combinedAccount,

		mode,
		initialPostData,
		initialWorkpiece,
		onDataChange,
	} = props;

	const { notify } = useNotifications();
	const { setActiveDialog } = useCommand();
	const { handleAddItem, addingItem } = useAddToCart(cartMessages);
	const searchParams = useSearchParams();
	const router = useRouter();
	const { executeAsync, isPending } = useBetterAction(updateItem);
	const [innerOpen, onInnerOpenChange] = useState(false);
	const [loginDialogOpen, setLoginDialogOpen] = useState<AddToProjectInput | boolean>(false);

	async function addToProject({ data, workpiece, name, id }: AddToProjectInput) {
		try {
			await onAddToProject(lang, data, workpiece, name, id === "newProject" ? undefined : id);

			notify({
				variant: "success",
				title: `${innerMessages.addedToProject}: ${name}`,
				subtitle: innerMessages.viewProject,
				action: {
					altText: innerMessages.viewProject,
					onClick: () =>
						void router.push(
							!id || id === "newProject" ? "/customer/projects" : `/customer/projects/${id}`,
						),
				},
			});
		} catch (error) {
			notify({
				variant: "danger",
				title: innerMessages.addToProjectError,
				subtitle: error instanceof Error ? error.message : undefined,
			});
		}
	}

	const groupedProduct = useMemo(() => {
		const id = item.product.groupedProductId ?? item.product.id;

		if (id === undefined) {
			throw new Error("groupedProduct is undefined");
		}

		return id;
	}, [item.product.groupedProductId, item.product.id]);

	const content = (
		<>
			<PanelConfiguratorComponent
				lang={lang}
				cartId={cartId}
				apiToken={apiToken}
				apiUrl={apiUrl}
				customer={customer ?? undefined}
				messages={messages}
				routes={routes}
				settings={settings}
				carrierCode={carrierCode}
				postcode={postcode ?? null}
				projects={projects}
				onAddToProject={async (data, workpiece, name, id) => {
					if (customer) {
						await addToProject({ data, workpiece, name, id });
					} else {
						setLoginDialogOpen(id === "newProject" ? { data, workpiece, name, id } : true);
					}
				}}
				mode={mode}
				productGroup={item.product.attributeSet as ProductGroup | null}
				groupedProduct={groupedProduct}
				initialWorkpiece={{
					...((mode === "editCartItem" || mode === "editProjectItem") && item.orderData
						? (JSON.parse(item.orderData) as Partial<Workpiece>)
						: undefined),
					...initialWorkpiece,
				}}
				initialPostData={{
					product: searchParams?.get("thickness") ?? item.product.id,
					quantity: mode === "editCartItem" || mode === "editProjectItem" ? item.quantity : 1,
					unit:
						mode === "editCartItem" || mode === "editProjectItem"
							? item.parent
								? ProductionUnit.CompletePanel
								: ProductionUnit.M2
							: undefined,
					...initialPostData,
				}}
				onDataChange={onDataChange}
				setActiveDialog={setActiveDialog}
				onAddToCart={async (data, workpiece) => {
					if (mode === "editCartItem") {
						if (!item.id) throw new Error("Item ID is missing");

						await executeAsync({
							lang: lang,
							cartItem: {
								id: item.id,
								quantity: data.quantity,
								type: data.unit === ProductionUnit.CompletePanel ? "CHILD" : "DEFAULT",
								data: JSON.stringify({ type: "Wood", ...workpiece }),
							},
						});
					} else if (mode === "editProjectItem") {
						if (!item.id) throw new Error("Item ID is missing");
						if (!item.product.id) throw new Error("Product ID is missing");

						await updateCustomerProjectItem({
							id: item.id,
							lang: lang,
							orderData: JSON.stringify({ type: "Wood", ...workpiece }),
							quantity: data.quantity,
							productId: item.product.id,
						});
					} else if (data.product) {
						await handleAddItem({
							lang: lang,
							cartItem: {
								id: data.product,
								quantity: data.quantity,
								type: data.unit === ProductionUnit.CompletePanel ? "CHILD" : "DEFAULT",
								data: JSON.stringify({ type: "Wood", ...workpiece }),
							},
						});
					}

					onOpenChange?.(false);
				}}
			/>

			<AddToProjectDialog
				{...combinedAccount}
				projects={{
					...combinedAccount.projects,
					innerMessages,
				}}
				lang={lang}
				input={loginDialogOpen}
				onOpenChange={() => setLoginDialogOpen(false)}
			/>
		</>
	);

	if (variant === "dialogButton") {
		return (
			<>
				<Slot.Root
					onClick={(e) => {
						e.stopPropagation();
						onInnerOpenChange(!innerOpen);
					}}
				>
					{children}
				</Slot.Root>

				<SideDialog
					color="shades-light"
					size="lg"
					open={addingItem || isPending ? true : innerOpen}
					onOpenChange={onInnerOpenChange}
					title={title}
				>
					{content}
				</SideDialog>
			</>
		);
	}

	if (variant === "dialog") {
		return (
			<SideDialog
				color="shades-light"
				size="lg"
				open={open}
				onOpenChange={onOpenChange}
				title={title}
			>
				{content}
			</SideDialog>
		);
	}

	return content;
}

type AddToProjectInput = { data: PostData; workpiece: PanelWorkpiece; name: string; id?: string };

type AddToProjectMode = "login" | "create" | "createBusiness" | "projects";

interface AddToProjectDialogProps {
	lang: string;
	onOpenChange: Dispatch<SetStateAction<boolean>>;
	input: AddToProjectInput | boolean;
	login: {
		title: string;
		description?: ReactNode;
		redirect: string;
		success: string;
		welcome: string;
		formContent: ReactNode;
	};
	create: {
		title: string;
		description?: ReactNode;
		success: string;
		businessSuccess: string;
		email: string;
		passwordMatch: string;
		formContent: ReactNode;
	};
	createBusiness: {
		title: string;
		description?: ReactNode;
		success: string;
		businessSuccess: string;
		email: string;
		passwordMatch: string;
		formContent: ReactNode;
	};
	projects: {
		title: string;
		description: string;
		projects: { id: string; name: string }[];
		label: string;
		nameForm: ReactNode;
		projectForm: ReactNode;
		children: ReactNode;
		innerMessages: {
			addedToProject: string;
			addToProjectError: string;
			viewProject: string;
		};
	};
	actions: {
		login: string;
		create: string;
		createBusiness: string;
	};
}

function AddToProjectDialog({
	lang,
	onOpenChange,
	input,
	login,
	create,
	createBusiness,
	projects,
	actions,
}: AddToProjectDialogProps) {
	const [mode, setMode] = useState<AddToProjectMode>("login");

	if (typeof input === "boolean") {
		return null;
	}

	return (
		<Dialog
			width={mode === "projects" ? "4xl" : undefined}
			open={!!input}
			onOpenChange={() => {
				setMode("login");
				onOpenChange((prev) => !prev);
			}}
			title={match(mode)
				.with("create", () => create.title)
				.with("createBusiness", () => createBusiness.title)
				.with("login", () => login.title)
				.otherwise(() => projects.title)}
			subtitle={match(mode)
				.with("create", () => create.description)
				.with("createBusiness", () => createBusiness.description)
				.with("login", () => login.description)
				.otherwise(() => projects.description)}
		>
			{match(mode)
				.with("create", () => (
					<>
						<CreateAccountForm
							lang={lang}
							labels={{
								success: create.success,
								businessSuccess: create.businessSuccess,
								email: create.email,
								passwordMatch: create.passwordMatch,
							}}
							onSubmit={() => setMode("projects")}
							actionAfterSuccess={null}
						>
							{create.formContent}
						</CreateAccountForm>

						<div className="mt-4 flex flex-col items-start gap-2">
							<Button starticon={faUserUnlock} variant="link" onClick={() => setMode("login")}>
								{actions.login}
							</Button>
							<Button
								starticon={faUserTie}
								variant="link"
								onClick={() => setMode("createBusiness")}
							>
								{actions.createBusiness}
							</Button>
						</div>
					</>
				))
				.with("createBusiness", () => (
					<>
						<CreateAccountForm
							lang={lang}
							labels={{
								success: createBusiness.success,
								businessSuccess: createBusiness.businessSuccess,
								email: createBusiness.email,
								passwordMatch: createBusiness.passwordMatch,
							}}
							businessAccount
							onSubmit={() => setMode("projects")}
							actionAfterSuccess={null}
						>
							{createBusiness.formContent}
						</CreateAccountForm>

						<div className="mt-4 flex flex-col items-start gap-2">
							<Button starticon={faUserUnlock} variant="link" onClick={() => setMode("login")}>
								{actions.login}
							</Button>
							<Button starticon={faUserPlus} variant="link" onClick={() => setMode("create")}>
								{actions.create}
							</Button>
						</div>
					</>
				))
				.with("login", () => (
					<>
						<ClientLoginForm
							lang={lang}
							messages={{
								redirect: login.redirect,
								success: login.success,
								welcome: login.welcome,
							}}
							onSubmit={() => setMode("projects")}
							actionAfterSuccess={null}
						>
							{login.formContent}
						</ClientLoginForm>

						<div className="mt-4 flex flex-col items-start gap-2">
							<Button starticon={faUserPlus} variant="link" onClick={() => setMode("create")}>
								{actions.create}
							</Button>
							<Button
								starticon={faUserTie}
								variant="link"
								onClick={() => setMode("createBusiness")}
							>
								{actions.createBusiness}
							</Button>
						</div>
					</>
				))
				.otherwise(() => {
					return <CustomerProjectForm {...projects} {...input} lang={lang} variant="item" />;
				})}
		</Dialog>
	);
}
